import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Footer from '../homepage/components/Footer';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ResourceSearch from '../../components/ui/ResourceSearch';
import { blogService } from '../../services/blogService';

const ResourcePage = ({ title: defaultTitle, subtitle: defaultSubtitle, targetPage, icon = 'BookOpen' }) => {
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageSettings, setPageSettings] = useState({
    title: defaultTitle,
    subtitle: defaultSubtitle,
    cta_title: 'Start selling on OpenGadget',
    cta_subtitle: 'Join our marketplace and turn your designs into reality with localized 3D manufacturing.'
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(9);

  const indexOfLastPost = currentPage * itemsPerPage;
  const indexOfFirstPost = indexOfLastPost - itemsPerPage;
  const currentPosts = blogPosts.slice(indexOfFirstPost, indexOfLastPost);
  const totalPages = Math.ceil(blogPosts.length / itemsPerPage);

  const getPageIcon = (page) => {
    switch (page) {
      case 'seller-toolbox': return 'Store';
      case 'documentation': return 'BookOpen';
      case 'api-reference': return 'Code';
      case 'tutorials': return 'Video';
      case 'resources-hub': return 'Globe';
      default: return 'FileText';
    }
  };

  useEffect(() => {
    const loadPageData = async () => {
      try {
        setLoading(true);
        const [posts, settings] = await Promise.all([
          blogService.getPublishedPosts({ targetPage: targetPage === 'blog' ? null : targetPage }),
          blogService.getPageSettings(targetPage)
        ]);
        setBlogPosts(posts);
        if (settings) setPageSettings(settings);
      } catch (error) {
        console.error(`Error fetching ${targetPage} data:`, error);
      } finally {
        setLoading(false);
      }
    };
    loadPageData();
  }, [targetPage]);

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      
      {/* Hero Section - DARK CONTRASTING BACKGROUND */}
      <div className="bg-slate-900 text-white pt-32 pb-20 border-b border-slate-800 shadow-inner">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl">
            <div className="inline-flex items-center justify-center p-3 bg-blue-600 rounded-xl mb-6 text-white shadow-lg shadow-blue-500/20">
              <Icon name={icon} size={32} />
            </div>
            <h1 className="text-4xl md:text-5xl font-extrabold mb-4 tracking-tight">{pageSettings.title}</h1>
            <p className="text-xl text-slate-300 mb-10 font-medium leading-relaxed">{pageSettings.subtitle}</p>
            <ResourceSearch placeholder={`Search ${pageSettings.title.toLowerCase()}...`} />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-6 py-16">
        <div className="flex justify-between items-center mb-10 text-left">
            <h2 className="text-2xl font-bold text-slate-800">Available Articles</h2>
            <Link to="/resources-hub" className="text-blue-600 hover:text-blue-700 font-bold flex items-center group bg-white px-4 py-2 rounded-lg shadow-sm border border-slate-100 transition-all hover:shadow-md">
              View All Articles
              <Icon name="ArrowRight" size={18} className="ml-2 group-hover:translate-x-1 transition-transform" />
            </Link>
        </div>

        {loading ? (
          <div className="grid md:grid-cols-3 gap-8 text-left">
            {[1, 2, 3].map(i => (
              <div key={i} className="animate-pulse bg-white rounded-2xl h-80 shadow-sm border border-slate-100" />
            ))}
          </div>
        ) : (
          <>
            {blogPosts?.length > 0 ? (
              <>
                <div className="grid md:grid-cols-3 gap-8 text-left">
                  {currentPosts.map((post) => (
                    <Link 
                      key={post.id} 
                      to={`/${post.target_page || post.targetPage || 'blog'}/${post.slug}`}
                      className="bg-white rounded-2xl shadow-lg border border-slate-100 overflow-hidden hover:shadow-xl transition-all group flex flex-col h-full"
                    >
                      <div className="h-48 bg-slate-100 overflow-hidden relative">
                        {post.featured_image ? (
                          <img src={post.featured_image} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-slate-300">
                            <Icon name={getPageIcon(post.target_page || post.targetPage)} size={48} />
                          </div>
                        )}
                        <div className="absolute top-4 left-4">
                          <span className="px-3 py-1 bg-blue-600 text-white text-xs font-bold rounded-full uppercase tracking-wider shadow-sm">
                            {post.category?.name}
                          </span>
                        </div>
                      </div>
                      <div className="p-6 flex-1 flex flex-col">
                        <h3 className="text-xl font-bold text-slate-800 mb-3 group-hover:text-blue-600 transition-colors line-clamp-2">
                          {post.title}
                        </h3>
                        <p className="text-slate-600 text-sm mb-4 line-clamp-3">
                          {post.excerpt}
                        </p>
                        <div className="mt-auto flex items-center text-blue-600 font-semibold text-sm">
                          Read Full Article
                          <Icon name="ArrowRight" size={16} className="ml-1 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
                
                {blogPosts.length > 9 && (
                  <div className="flex flex-col md:flex-row justify-between items-center mt-12 gap-4">
                    <div className="flex items-center text-sm text-slate-500">
                      <span className="mr-2">Show:</span>
                      <select 
                        value={itemsPerPage} 
                        onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                        className="border-slate-300 rounded-md text-slate-700 text-sm focus:ring-blue-500 focus:border-blue-500 py-1"
                      >
                        <option value={9}>9</option>
                        <option value={18}>18</option>
                        <option value={36}>36</option>
                      </select>
                      <span className="ml-2">per page</span>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Button 
                        variant="outline" 
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                        size="sm"
                      >
                        Previous
                      </Button>
                      <span className="text-sm font-medium text-slate-700 px-4">
                        Page {currentPage} of {totalPages}
                      </span>
                      <Button 
                        variant="outline" 
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        disabled={currentPage === totalPages}
                        size="sm"
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="py-20 text-center bg-white rounded-2xl border border-dashed border-slate-200">
                <div className="inline-flex items-center justify-center p-4 bg-slate-50 rounded-full mb-4 text-slate-400">
                  <Icon name={getPageIcon(targetPage)} size={48} />
                </div>
                <h3 className="text-xl font-bold text-slate-800 mb-2">No articles found</h3>
                <p className="text-slate-500 max-w-md mx-auto">
                  We're currently preparing new content for this section. Please check back soon for updates.
                </p>
              </div>
            )}
          </>
        )}

        {/* CTA Section - DYNAMIC FROM DATABASE */}
        <div className="mt-20 bg-gradient-to-r from-blue-600 to-purple-600 rounded-3xl p-12 text-center text-white shadow-2xl relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -mr-32 -mt-32 blur-3xl"></div>
          <div className="relative z-10">
            <h3 className="text-3xl font-extrabold mb-4">{pageSettings.cta_title}</h3>
            <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto font-medium">
              {pageSettings.cta_subtitle}
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link to="/seller-signup">
                <Button variant="primary" className="w-full sm:w-auto bg-white text-blue-600 hover:bg-slate-50 text-lg px-8 py-3">
                  Become a Seller
                </Button>
              </Link>
              <Link to="/documentation">
                <Button variant="outline" className="w-full sm:w-auto border-white text-white hover:bg-white/10 text-lg px-8 py-3">
                  Read the Docs
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ResourcePage;
